import REST_CLIENT from '../utils/restClient';
import getClientConfig from '../utils/getClientConfig';
import { ACTION_TYPES as AUTHENTICATION_ACTION_TYPES } from './authentication';
import Logger, { LOG_LEVELS } from '../utils/logger';

export const MODULE_NAME = 'IAM_USERS_CONTROLLER';
const log = new Logger(MODULE_NAME, LOG_LEVELS.DEBUG);

function namespaced(name) {
    return `${MODULE_NAME}.${name}`;
}

const LOAD = namespaced('LOAD');
const LOAD_OK = namespaced('LOAD_OK');
const LOAD_FAIL = namespaced('LOAD_FAIL');
const INVALIDATE = namespaced('INVALIDATE');

export const ACTION_TYPES = {
    LOAD,
    LOAD_OK,
    LOAD_FAIL,
    INVALIDATE,
};

const INVALID_IAM_USERS_RESPONSE = 'Invalid users response';
const INVALID_IAM_SYSTEM_RESPONSE = 'Invalid IAM system response';

export const fetchAllIamUsers = async (iamSystemId, iamTenantId, batchSize) => {
    const users = [];
    let skip = 0;

    while (true) {
        const batch = await REST_CLIENT.getIamUsers(iamSystemId, iamTenantId, batchSize, skip);
        if (!batch?.items) {
            throw new Error(INVALID_IAM_USERS_RESPONSE);
        }

        if (batch.items.length === 0) {
            return users;
        }

        users.push(...batch.items);

        if (batch.items.length < batchSize) {
            return users;
        }

        skip += batchSize;
    }
};

export const loadIamUsersForTenant = async (iamTenantId) => {
    const { iamNamespace, iamUsersBatchSize } = getClientConfig();
    const iamSystemResponse = await REST_CLIENT.getIamSystemId(iamNamespace);
    const iamSystemId = iamSystemResponse?.items?.[0]?.id;

    if (!iamSystemId) {
        throw new Error(INVALID_IAM_SYSTEM_RESPONSE);
    }

    return fetchAllIamUsers(iamSystemId, iamTenantId, iamUsersBatchSize);
};

const initialState = {
    data: undefined,
    loading: false,
    error: null,
    tenantId: null,
};

let inFlightLoad = null;
let inFlightTenantId = null;

const clearInFlightLoad = () => {
    inFlightLoad = null;
    inFlightTenantId = null;
};

export const __resetInFlightLoadForTests = clearInFlightLoad;

const isCachedForTenant = (state, iamTenantId) =>
    state.tenantId === iamTenantId && !!state.data;

export const updateSubStateFromAction = (state = initialState, action) => {
    log.debug('updateSubStateFromAction: ', action, state);
    switch (action.type) {
        case LOAD:
            return Object.assign({}, state, {
                loading: true,
                error: null,
                tenantId: action.tenantId ?? state.tenantId,
            });
        case LOAD_OK:
            return Object.assign({}, state, {
                data: action.response,
                loading: false,
                error: null,
                tenantId: action.tenantId ?? state.tenantId,
            });
        case LOAD_FAIL:
            return Object.assign({}, state, {
                data: undefined,
                loading: false,
                error: action.error,
            });
        case INVALIDATE:
        case AUTHENTICATION_ACTION_TYPES.LOGOUT_OK:
            return Object.assign({}, initialState);
        default:
            return state;
    }
};

const fetchAndStoreIamUsers = async (dispatch, iamTenantId) => {
    dispatch({ type: LOAD, tenantId: iamTenantId });
    inFlightTenantId = iamTenantId;

    try {
        const response = await loadIamUsersForTenant(iamTenantId);
        dispatch({ type: LOAD_OK, response, tenantId: iamTenantId });
        return response;
    } catch (error) {
        dispatch({ type: LOAD_FAIL, error });
        throw error;
    } finally {
        if (inFlightTenantId === iamTenantId) {
            clearInFlightLoad();
        }
    }
};

export const API = {
    doLoad: (iamTenantId, { force = false } = {}) => {
        return async (dispatch, getState) => {
            const cached = getState().iamUsers || initialState;

            if (!force && isCachedForTenant(cached, iamTenantId)) {
                return cached.data;
            }

            if (!force && inFlightLoad && inFlightTenantId === iamTenantId) {
                return inFlightLoad;
            }

            inFlightLoad = fetchAndStoreIamUsers(dispatch, iamTenantId);
            return inFlightLoad;
        };
    },

    doEnsureLoaded: (iamTenantId, options = {}) => {
        return (dispatch) => dispatch(API.doLoad(iamTenantId, options));
    },

    doInvalidate: () => {
        return (dispatch) => {
            clearInFlightLoad();
            dispatch({ type: INVALIDATE });
        };
    },
};

export default API;
